/**
 * en-GB messages added by Phase 4 task W26 (docs/PHASE-4-PLAN.md). Only that task edits this file.
 * Flat keys, `{name}` placeholders, merged into enGB by ./index.ts.
 * Billing pages: platform (plans, subscriptions, invoices, credit notes, payments, export) and the tenant's own billing page.
 */
export const w26EnGB = {
    // Shared
    'billingUi.remove': 'Remove',
    'billingUi.none': 'None',
    'billingUi.noPlan': 'No plan',
    'billingUi.noSubscription': 'No subscription',
    'billingUi.unlimited': 'No limit',
    'billingUi.money.invalid': 'Enter an amount in pounds, for example 12.50.',
    'billingUi.overdueDays': 'oldest {count} days late',
    'billingUi.subscriptionFor': 'Subscription for {name}',
    'billingUi.invoicesFor': 'Invoices for {name}',
    'billingUi.tabs': 'Billing sections',
    'billingUi.tabs.overview': 'Customers',
    'billingUi.tabs.plans': 'Plans',
    'billingUi.tabs.invoices': 'Invoices',
    'billingUi.tabs.export': 'Export',
    'billingUi.col.customer': 'Customer',
    'billingUi.col.plan': 'Plan',
    'billingUi.col.nextInvoice': 'Next invoice',
    'billingUi.col.overdue': 'Overdue',
    'billingUi.col.subscription': 'Subscription',
    'billingUi.col.number': 'Number',
    'billingUi.col.issued': 'Issued',
    'billingUi.col.due': 'Due',
    'billingUi.col.total': 'Total',
    'billingUi.col.balance': 'Balance',
    'billingUi.period.monthly': 'Monthly',
    'billingUi.period.annual': 'Annual',
    'billingUi.invoiceStatus.draft': 'Draft',
    'billingUi.invoiceStatus.issued': 'Issued',
    'billingUi.invoiceStatus.paid': 'Paid',
    'billingUi.invoiceStatus.credited': 'Credited',
    'billingUi.invoiceStatus.void_draft': 'Voided draft',
    'billingUi.subStatus.trial': 'Trial',
    'billingUi.subStatus.active': 'Active',
    'billingUi.subStatus.overdue': 'Overdue',
    'billingUi.subStatus.suspended': 'Suspended',
    'billingUi.subStatus.cancelled': 'Cancelled',
    'billingUi.restriction.none': 'No restriction',
    'billingUi.restriction.kiosk_grace': 'Kiosks still work for now',
    'billingUi.restriction.read_only': 'Portal is read-only',
    'billingUi.kind.invoice': 'Invoice',
    'billingUi.kind.credit_note': 'Credit note',
    'billingUi.method.bank_transfer': 'Bank transfer',
    'billingUi.method.card': 'Card',
    'billingUi.method.direct_debit': 'Direct Debit',
    'billingUi.method.cheque': 'Cheque',
    'billingUi.method.other': 'Other',
    'billingUi.metric.visits': 'Visits',
    'billingUi.metric.sms_sent': 'Text messages sent',
    'billingUi.metric.emails_sent': 'Emails sent',
    'billingUi.metric.storage_bytes': 'Storage',
    'billingUi.metric.active_users': 'Active users',
    'billingUi.metric.kiosk_devices': 'Kiosks',
    'billingUi.metric.sites': 'Sites',
    'billingUi.metric.ai_questions': 'AI questions',
    // Platform: customers overview
    'billingUi.platform.title': 'Billing',
    'billingUi.platform.description': 'Plans, subscriptions and invoices for every customer.',
    'billingUi.overview.run': 'Run invoice run now',
    'billingUi.overview.runDone': 'Invoice run finished. Drafts created: {count}.',
    'billingUi.overview.searchHint': 'Search by customer name.',
    'billingUi.overview.table': 'Customers with their plan, status and overdue balance',
    // Platform: plans
    'billingUi.plans.title': 'Plans',
    'billingUi.plans.description': 'What each plan costs and includes. Price changes apply from each customer\'s next period; issued invoices never change.',
    'billingUi.plans.table': 'Plans',
    'billingUi.plans.new': 'New plan',
    'billingUi.plans.edit': 'Edit {name}',
    'billingUi.plans.editLabel': 'Edit plan {name}',
    'billingUi.plans.saved': 'Plan saved.',
    'billingUi.plans.name': 'Name',
    'billingUi.plans.code': 'Code',
    'billingUi.plans.codeHint': 'Lower-case letters, numbers and hyphens. It can\'t be changed later.',
    'billingUi.plans.monthly': 'Monthly price (£)',
    'billingUi.plans.annual': 'Annual price (£)',
    'billingUi.plans.allowances': 'Included in the price',
    'billingUi.plans.allowancesHelp': 'Anything above these amounts is charged at the extra prices below.',
    'billingUi.plans.sites': 'Sites',
    'billingUi.plans.kiosks': 'Kiosks',
    'billingUi.plans.sms': 'Texts per month',
    'billingUi.plans.storage': 'Storage (GB)',
    'billingUi.plans.modules': 'Modules included',
    'billingUi.plans.modulesHint': 'Module keys separated by commas. Other modules a customer turns on are charged as add-ons.',
    'billingUi.plans.extras': 'Extra prices',
    'billingUi.plans.extrasHelp': 'Per month. Annual plans pay twelve times these amounts on the annual invoice.',
    'billingUi.plans.perSite': 'Per extra site (£)',
    'billingUi.plans.perKiosk': 'Per extra kiosk (£)',
    'billingUi.plans.perStorage': 'Per extra GB (£)',
    'billingUi.plans.smsBundle': 'Texts in one bundle',
    'billingUi.plans.perSmsBundle': 'Price per bundle (£)',
    'billingUi.plans.addOnPrices': 'Add-on module prices (per month)',
    'billingUi.plans.addOnKey': 'Module key',
    'billingUi.plans.addOnPrice': 'Price (£)',
    'billingUi.plans.addAddOn': 'Add a module price',
    'billingUi.plans.removeAddOn': 'Remove module price {n}',
    'billingUi.plans.active': 'Plan is available to new subscriptions',
    'billingUi.plans.activeHint': 'Turn off to retire a plan. Existing customers keep it.',
    'billingUi.plans.priceNote': 'Price changes apply from each subscription\'s next period.',
    'billingUi.plans.includes': 'Includes',
    'billingUi.plans.customers': 'Customers',
    'billingUi.plans.summary': '{sites} sites, {kiosks} kiosks, {sms} texts, {gb} GB',
    'billingUi.plans.statusActive': 'Available',
    'billingUi.plans.statusRetired': 'Retired',
    // Platform: subscription
    'billingUi.sub.title': 'Subscription: {name}',
    'billingUi.sub.description': 'The plan, discount and add-ons this customer is billed on.',
    'billingUi.sub.back': 'Back to customers',
    'billingUi.sub.none': 'This customer has no subscription yet.',
    'billingUi.sub.summary': 'Current subscription',
    'billingUi.sub.period': 'Billing period',
    'billingUi.sub.currentPeriod': 'Current period',
    'billingUi.sub.range': '{from} to {to}',
    'billingUi.sub.trialEnds': 'Trial ends',
    'billingUi.sub.restriction': 'Restriction',
    'billingUi.sub.pending': 'Booked change',
    'billingUi.sub.pendingText': 'A new plan ({period}) starts at the next period.',
    'billingUi.sub.cancelled': 'Cancelled on',
    'billingUi.sub.createTitle': 'Start a subscription',
    'billingUi.sub.createHelp': 'Choose a plan. The first invoice is drafted when the trial ends.',
    'billingUi.sub.create': 'Create subscription',
    'billingUi.sub.created': 'Subscription created.',
    'billingUi.sub.choosePlan': 'Choose a plan',
    'billingUi.sub.startDate': 'Start date',
    'billingUi.sub.trialDays': 'Free trial (days)',
    'billingUi.sub.trialHint': 'Leave empty for the default. 0 means no trial.',
    'billingUi.sub.discount': 'Discount (%)',
    'billingUi.sub.discountNote': 'Discount note',
    'billingUi.sub.editTitle': 'Discount and add-ons',
    'billingUi.sub.editHelp': 'Changes apply to the next invoice.',
    'billingUi.sub.saved': 'Subscription saved.',
    'billingUi.sub.addOns': 'Add-on modules (price per month)',
    'billingUi.sub.changeTitle': 'Change plan',
    'billingUi.sub.changeHelp': 'Changing now charges or credits the difference by days. Changing at the period end changes nothing until then.',
    'billingUi.sub.change': 'Change plan',
    'billingUi.sub.newPlan': 'New plan',
    'billingUi.sub.effective': 'When',
    'billingUi.sub.atPeriodEnd': 'At the end of the period',
    'billingUi.sub.now': 'Now',
    'billingUi.sub.planChanged': 'Plan change saved.',
    'billingUi.sub.cancelTitle': 'Cancel subscription',
    'billingUi.sub.cancelHelp': 'Nothing is deleted. Invoices already issued stay as they are.',
    'billingUi.sub.reason': 'Reason',
    'billingUi.sub.cancel': 'Cancel subscription',
    'billingUi.sub.cancelConfirm': 'Yes, cancel it',
    'billingUi.sub.cancelDone': 'Subscription cancelled.',
    'billingUi.sub.seeInvoices': 'See this customer\'s invoices',
    // Platform: invoices
    'billingUi.invoices.title': 'Invoices',
    'billingUi.invoices.description': 'Invoices and credit notes. Review drafts here, then issue them.',
    'billingUi.invoices.table': 'Invoices and credit notes',
    'billingUi.invoices.searchHint': 'Search by number or customer.',
    'billingUi.invoices.kind': 'Type',
    'billingUi.invoices.allKinds': 'Invoices and credit notes',
    'billingUi.invoices.allStatuses': 'All statuses',
    'billingUi.invoices.overdueOnly': 'Overdue only',
    'billingUi.invoices.reviewDrafts': 'Review drafts',
    'billingUi.invoices.filteredToCustomer': 'Showing one customer only.',
    'billingUi.invoices.clearCustomer': 'Show all customers',
    'billingUi.invoices.open': 'Open {label} for {name}',
    'billingUi.invoice.title': 'Invoice {number}',
    'billingUi.invoice.creditNoteTitle': 'Credit note {number}',
    'billingUi.invoice.draftTitle': 'Draft invoice',
    'billingUi.invoice.draftNote': 'This is a draft. It has no number yet and the customer has not seen it. Check the lines, then issue it.',
    'billingUi.invoice.readOnly': 'Issued invoices can\'t be changed. To correct one, issue a credit note.',
    'billingUi.invoice.issuedOn': 'Issued {date}',
    'billingUi.invoice.dueOn': 'Due {date}',
    'billingUi.invoice.linesTable': 'Invoice lines',
    'billingUi.invoice.lineDescription': 'Description',
    'billingUi.invoice.quantity': 'Quantity',
    'billingUi.invoice.unitPrice': 'Unit price (£)',
    'billingUi.invoice.amount': 'Amount (£)',
    'billingUi.invoice.subtotal': 'Subtotal',
    'billingUi.invoice.vat': 'VAT at {rate}',
    'billingUi.invoice.total': 'Total',
    'billingUi.invoice.paid': 'Paid',
    'billingUi.invoice.credited': 'Credited',
    'billingUi.invoice.balance': 'Balance',
    'billingUi.invoice.usage': 'Usage this period',
    'billingUi.invoice.usageTable': 'Usage counted for this invoice',
    'billingUi.usage.metric': 'Item',
    'billingUi.usage.used': 'Used',
    'billingUi.usage.allowance': 'Included',
    'billingUi.usage.charged': 'Charged units',
    'billingUi.invoice.billTo': 'Billed to {name} ({email}).',
    'billingUi.invoice.po': 'Purchase order {po}.',
    'billingUi.invoice.addLine': 'Add a line',
    'billingUi.invoice.addLineHelp': 'Use a negative price for a deduction. Lines can only be added to drafts.',
    'billingUi.invoice.addLineButton': 'Add line',
    'billingUi.invoice.unitHint': 'Pounds. A minus sign takes money off.',
    'billingUi.invoice.lineAdded': 'Line added.',
    'billingUi.invoice.issue': 'Issue invoice',
    'billingUi.invoice.issueConfirm': 'Issue and email it',
    'billingUi.invoice.issuedToast': 'Invoice {number} issued.',
    'billingUi.invoice.void': 'Discard draft',
    'billingUi.invoice.voidConfirm': 'Yes, discard it',
    'billingUi.invoice.voided': 'Draft discarded.',
    'billingUi.invoice.recordPayment': 'Record a payment',
    'billingUi.invoice.recordPaymentHelp': 'Use this when the customer has paid by bank transfer or another way outside CloudSign.',
    'billingUi.invoice.recordPaymentButton': 'Record payment',
    'billingUi.invoice.paymentRecorded': 'Payment recorded.',
    'billingUi.invoice.paidOn': 'Date paid',
    'billingUi.invoice.method': 'Method',
    'billingUi.invoice.reference': 'Reference',
    'billingUi.invoice.creditTitle': 'Issue a credit note',
    'billingUi.invoice.creditHelp': 'A credit note has its own number and reduces the balance. The invoice itself stays as issued.',
    'billingUi.invoice.creditReason': 'Reason',
    'billingUi.invoice.creditFull': 'Credit the whole remaining amount',
    'billingUi.invoice.creditAmount': 'Amount to credit (£)',
    'billingUi.invoice.addCreditLine': 'Add a credit line',
    'billingUi.invoice.removeCreditLine': 'Remove credit line {n}',
    'billingUi.invoice.creditButton': 'Issue credit note',
    'billingUi.invoice.creditConfirm': 'Yes, issue it',
    'billingUi.invoice.creditIssued': 'Credit note issued.',
    // Platform: export
    'billingUi.export.title': 'Export for accounts',
    'billingUi.export.description': 'Download invoices or payments as a CSV file for your accounts package.',
    'billingUi.export.what': 'What to export',
    'billingUi.export.invoices': 'Invoices and credit notes',
    'billingUi.export.payments': 'Payments',
    'billingUi.export.from': 'From',
    'billingUi.export.to': 'To',
    'billingUi.export.rangeError': 'The end date must be on or after the start date.',
    'billingUi.export.download': 'Download CSV',
    // Tenant page
    'billingUi.tenant.title': 'Billing',
    'billingUi.tenant.description': 'Your plan, what you have used, and your invoices.',
    'billingUi.tenant.planTitle': 'Your plan',
    'billingUi.tenant.noPlan': 'There is no plan on your account yet. Contact us if you think that is wrong.',
    'billingUi.tenant.priceMonthly': '{price} per month, excluding VAT',
    'billingUi.tenant.priceAnnual': '{price} per year, excluding VAT',
    'billingUi.tenant.trialEnds': 'Free trial ends {date}',
    'billingUi.tenant.nextInvoice': 'Next invoice {date}',
    'billingUi.tenant.discount': 'Discount: {percent}%',
    'billingUi.tenant.balanceTitle': 'Balance',
    'billingUi.tenant.balanceLabel': 'Amount due: {amount}',
    'billingUi.tenant.balanceDue': 'Amount due on issued invoices.',
    'billingUi.tenant.balanceClear': 'Nothing is due. Thank you.',
    'billingUi.tenant.bankTransfer': 'Please pay by bank transfer using the details and invoice number on your invoice.',
    'billingUi.tenant.updatePayment': 'Update your card or Direct Debit',
    'billingUi.tenant.usageTitle': 'Usage this period',
    'billingUi.tenant.usageHelp': 'Your usage against what your plan includes.',
    'billingUi.tenant.noUsage': 'No usage to show yet.',
    'billingUi.tenant.usedOf': '{used} of {allowance}',
    'billingUi.tenant.over': 'Over your allowance',
    'billingUi.tenant.invoicesTitle': 'Invoices',
    'billingUi.tenant.invoicesTable': 'Your invoices and credit notes',
    'billingUi.tenant.noInvoices': 'No invoices yet.',
    'billingUi.tenant.pdf': 'PDF',
    'billingUi.tenant.downloadPdf': 'Download PDF for {number}',
    'billingUi.tenant.contactTitle': 'Billing contact',
    'billingUi.tenant.contactHelp': 'Invoices are emailed to this contact and show these details.',
    'billingUi.tenant.contactName': 'Contact name',
    'billingUi.tenant.contactEmail': 'Contact email',
    'billingUi.tenant.contactEmailHint': 'Leave empty to use your first administrator.',
    'billingUi.tenant.emailInvalid': 'Enter a valid email address.',
    'billingUi.tenant.organisation': 'Organisation name on invoices',
    'billingUi.tenant.address': 'Billing address',
    'billingUi.tenant.purchaseOrder': 'Purchase order',
    'billingUi.tenant.purchaseOrderHint': 'Shown on every invoice if you fill it in.',
    'billingUi.tenant.contactSaved': 'Billing contact saved.',
};
//# sourceMappingURL=w26.js.map